import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, AlertTriangle, Loader } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import SEO from "./SEO";
import { useTheme } from "../context/ThemeContext";
import { verifyDirectoryListing } from "../services/directoryService";
import "./Directory.css";

/**
 * DirectoryVerify - landing page for the link in the directory verification
 * email. It reads the single-use token from the query string, confirms it with
 * the API, and reports the outcome.
 */
export default function DirectoryVerify() {
  const { darkMode } = useTheme();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [state, setState] = useState("verifying");
  const [message, setMessage] = useState("");
  // React 18 StrictMode double-invokes effects in development, and the token is
  // single-use — the second call would report "already used".
  const requestedRef = useRef(false);

  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;

    if (!token) {
      setState("error");
      setMessage("This link is missing its verification token.");
      return;
    }

    verifyDirectoryListing(token)
      .then((res) => {
        setState("success");
        setMessage(res.message || "Your email address has been confirmed.");
      })
      .catch((err) => {
        setState("error");
        setMessage(err.message || "We could not verify this listing.");
      });
  }, [token]);

  const isSuccess = state === "success";
  const isVerifying = state === "verifying";

  return (
    <div className={`directory-container ${darkMode ? "dark" : ""}`}>
      <SEO
        title="Confirm Your Directory Listing | LawFusion"
        path="/directory/verify"
        description="Confirm your email address to publish your LawFusion directory listing."
        noIndex={true}
      />
      <Header />

      <main className="directory-content">
        <section
          style={{
            maxWidth: "560px",
            margin: "0 auto",
            padding: "64px 0",
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 22px",
              background: isVerifying
                ? darkMode
                  ? "rgba(148,163,184,0.15)"
                  : "#e2e8f0"
                : isSuccess
                  ? darkMode
                    ? "rgba(34,197,94,0.18)"
                    : "#dcfce7"
                  : darkMode
                    ? "rgba(239,68,68,0.18)"
                    : "#fee2e2",
              color: isVerifying ? "#64748b" : isSuccess ? "#16a34a" : "#dc2626",
            }}
          >
            {isVerifying ? (
              <Loader className="spin" size={28} />
            ) : isSuccess ? (
              <Check size={30} strokeWidth={2.5} />
            ) : (
              <AlertTriangle size={28} />
            )}
          </div>

          <h1
            style={{
              fontSize: "26px",
              fontWeight: 800,
              margin: "0 0 12px",
              color: darkMode ? "#f8fafc" : "#1a2456",
            }}
          >
            {isVerifying
              ? "Confirming your email…"
              : isSuccess
                ? "Email Confirmed"
                : "We Couldn't Confirm This Link"}
          </h1>

          <p
            style={{
              fontSize: "15.5px",
              lineHeight: 1.5,
              margin: "0 0 32px",
              color: darkMode ? "#94a3b8" : "#6b7280",
            }}
          >
            {isVerifying
              ? "This will only take a moment."
              : message}
          </p>

          {!isVerifying && (
            <Link
              to="/directory"
              className="btn-join"
              style={{ textDecoration: "none", display: "inline-block" }}
            >
              {isSuccess ? "Browse the Directory" : "Back to the Directory"}
            </Link>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}
